/**
 * LayoutRolesModal — modal multiselect ról dla menu layoutu (Etap C, ADR-0031).
 *
 * Admin (z permission crm_core.menu_layouts.edit) wybiera role które mają
 * widzieć ten layout. Submit → PUT /api/menu/layouts/:id/role-assignments
 * z expectedVersion (OCC).
 *
 * Stany:
 *   - loading: czeka na role_templates + current role_assignments.
 *   - empty roles: brak roli w tenancie → komunikat info.
 *   - normal: lista ról z checkboxami, „Zaznaczono N", przycisk Zapisz.
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Modal } from '@/components/Modal';

/**
 * Generic role option — wspólny shape dla tenant role templates + admin role enum.
 * - Tenant: caller mapuje RoleTemplate {id, name, isSystem} → {id, label: name, isSystem}.
 * - Admin: caller buduje ADMIN_ROLES → {id: 'super_admin', label: t('roles.super_admin')}.
 */
export interface RoleOption {
  id: string;
  label: string;
  /** Tag „(system)" obok labelu. Tenant role templates używają, admin role nie. */
  isSystem?: boolean;
}

/**
 * Strukturalny shape minimum dla React Query result. Akceptuje pełne UseQueryResult
 * jak i ręcznie zbudowany obiekt (admin scope buduje statyczny rolesQuery z ADMIN_ROLES).
 */
export interface RolesQueryLike {
  data: { items: RoleOption[] } | undefined;
  isLoading: boolean;
}

export interface AssignmentsQueryLike {
  data: { roleIds: string[] } | undefined;
  isLoading: boolean;
}

export interface LayoutRolesModalProps {
  open: boolean;
  layoutName: string;
  /** Wymagany expectedVersion (OCC) — caller dostarcza obecną wersję layoutu. */
  expectedVersion: number;
  onSubmit: (roleIds: string[]) => void;
  onClose: () => void;
  submitting?: boolean;
  serverError?: string | null;
  /**
   * i18n key prefix (np. 'tenant.menuLayoutBuilder' albo 'admin.menuLayoutBuilder').
   * Default: 'tenant.menuLayoutBuilder' (back-compat).
   */
  i18nPrefix?: string;
  /**
   * Lista dostępnych ról — wstrzykiwana przez caller per scope.
   * Tenant: useRoleTemplates() zaadaptowany na RoleOption. Admin: ADMIN_ROLES static.
   */
  rolesQuery: RolesQueryLike;
  /**
   * Bieżące assignmenty layoutu (z backendu /:id/role-assignments).
   * Tenant: useLayoutRoleAssignments(). Admin: useAdminLayoutRoleAssignments().
   */
  assignmentsQuery: AssignmentsQueryLike;
}

export function LayoutRolesModal({
  open,
  layoutName,
  expectedVersion,
  onSubmit,
  onClose,
  submitting,
  serverError,
  i18nPrefix = 'tenant.menuLayoutBuilder',
  rolesQuery,
  assignmentsQuery,
}: LayoutRolesModalProps) {
  const { t } = useTranslation();

  const [selected, setSelected] = useState<Set<string>>(new Set());

  // Reset selected gdy modal się otwiera albo gdy fetched assignments się zmieniają.
  useEffect(() => {
    if (!open) return;
    if (assignmentsQuery.data) {
      setSelected(new Set(assignmentsQuery.data.roleIds));
    }
  }, [open, assignmentsQuery.data]);

  const isLoading = rolesQuery.isLoading || assignmentsQuery.isLoading;
  const allRoles: RoleOption[] = rolesQuery.data?.items ?? [];

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectedCount = useMemo(() => selected.size, [selected]);

  const handleSubmit = () => {
    onSubmit(Array.from(selected));
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t(`${i18nPrefix}.layouts.rolesModal.title`)}
      description={layoutName}
      size="md"
      footer={
        <>
          <span className="mr-auto text-sm text-text-tertiary" data-testid="roles-modal-count">
            {t(`${i18nPrefix}.layouts.rolesModal.selectedCount`, {
              count: selectedCount,
            })}
          </span>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="rounded-lg border border-border-secondary px-4 py-2 text-sm text-text-primary hover:bg-bg-secondary disabled:opacity-50"
            data-testid="roles-modal-cancel"
          >
            {t(`${i18nPrefix}.layouts.rolesModal.cancel`)}
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting || isLoading}
            className="rounded-lg bg-accent-600 px-4 py-2 text-sm text-white hover:bg-accent-700 disabled:opacity-50"
            data-testid="roles-modal-submit"
          >
            {t(`${i18nPrefix}.layouts.rolesModal.submit`)}
          </button>
        </>
      }
    >
      <div className="space-y-3">
        <p className="text-sm text-text-secondary">{t(`${i18nPrefix}.layouts.rolesModal.help`)}</p>

        {isLoading ? (
          <p className="text-sm text-text-tertiary" data-testid="roles-modal-loading">
            {t(`${i18nPrefix}.layouts.rolesModal.loading`)}
          </p>
        ) : allRoles.length === 0 ? (
          <p className="text-sm text-text-tertiary" data-testid="roles-modal-empty">
            {t(`${i18nPrefix}.layouts.rolesModal.noRoles`)}
          </p>
        ) : (
          <ul
            className="max-h-72 space-y-1 overflow-y-auto rounded border border-border-secondary p-2"
            data-testid="roles-modal-list"
          >
            {allRoles.map((role) => (
              <li key={role.id}>
                <label
                  className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 hover:bg-bg-secondary"
                  data-testid={`roles-modal-item-${role.id}`}
                >
                  <input
                    type="checkbox"
                    checked={selected.has(role.id)}
                    onChange={() => toggle(role.id)}
                    className="h-4 w-4 rounded border-border-secondary text-accent-600"
                  />
                  <span className="text-sm text-text-primary">{role.label}</span>
                  {role.isSystem ? (
                    <span className="text-xs text-text-tertiary">(system)</span>
                  ) : null}
                </label>
              </li>
            ))}
          </ul>
        )}

        {serverError ? (
          <p className="text-sm text-red-600" data-testid="roles-modal-error">
            {serverError}
          </p>
        ) : null}

        {/* expectedVersion hidden — caller send to PUT, tutaj tylko reference. */}
        <input type="hidden" data-testid="roles-modal-expected-version" value={expectedVersion} />
      </div>
    </Modal>
  );
}
